import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { InvoiceType, StatusType } from "../../types/entitiesModel";
import { useNavigate } from "react-router-dom";
import RuleIcon from "@mui/icons-material/Rule";
import { fetchStatusForInvoicesSelect } from "../../utils/Utils";
import { AuthContext } from "../../context/AuthContext";

interface AppTableProps {
  data: InvoiceType[];
  selectedStatus: StatusType;
  setSelectedStatus: (value: StatusType) => void;
}

const AppTableInvoices: React.FC<AppTableProps> = (props: AppTableProps) => {
  const { token } = React.useContext(AuthContext) || {};
  const navigate = useNavigate();

  const [status, setStatus] = React.useState<StatusType[]>([]);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleUpdateInvoice = (id: number) => {
    navigate(`/admin/invoices/validInvoice/${id.toString()}`);
  };

  React.useEffect(() => {
    if (token) {
      fetchStatusForInvoicesSelect(setLoading, setStatus, setError, token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      {error && (
        <Alert
          sx={{
            mx: "auto",
            my: 2,
            width: "95%",
          }}
          severity="error"
        >
          {error}
        </Alert>
      )}
      {!loading ? (
        <Box>
          <Card variant="outlined">
            <CardContent>
              {/* Contenedor para título y selector de estatus */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h3">Facturas</Typography>
                <TextField
                  id="status_id"
                  variant="outlined"
                  select
                  label="Status"
                  name="status_id"
                  value={props.selectedStatus.id}
                  onChange={(e) => {
                    props.setSelectedStatus({
                      id: e.target.value,
                      description: e.target.name,
                    });
                  }}
                  sx={{
                    width: { xs: "100%", md: "200px" },
                    mt: { xs: 2, md: 0 },
                  }}
                >
                  <MenuItem value={"no_status"}>Selecciona un status</MenuItem>
                  {Array.isArray(status) && status.length > 0 ? (
                    status.map((stat: StatusType) => (
                      <MenuItem key={stat.id} value={stat.id}>
                        {stat.description}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No hay status disponibles
                    </MenuItem>
                  )}
                </TextField>
              </Box>

              <Box
                sx={{
                  overflowX: "auto", 
                  width: "100%",
                }}
              >
                <Table
                  aria-label="simple table"
                  sx={{
                    mt: 3,
                    whiteSpace: "nowrap",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <Typography color="textSecondary" variant="h6">
                          Validar
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Id
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Método
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Representante
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Estudiante
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Curso
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Status
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Monto
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Pagado?
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography color="textSecondary" variant="h6">
                          Fecha
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {props.data.length > 0 ? (
                      props.data.map((invoice: InvoiceType) => (
                        <TableRow key={invoice.id}>
                          <TableCell align="center">
                            <Fab
                              color="success"
                              aria-label="valid"
                              disabled={invoice.status_id !== "processing"}
                              sx={{
                                width: 35,
                                height: 35,
                                cursor: "pointer",
                                backgroundColor: "green",
                                "&:hover": {
                                  backgroundColor: "darkgreen",
                                },
                              }}
                              onClick={() => {
                                handleUpdateInvoice(invoice.id);
                              }}
                            >
                              <RuleIcon
                                sx={{
                                  width: 20,
                                  height: 20,
                                  color: "white",
                                }}
                              />
                            </Fab>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.payment
                                ? invoice.payment.account_name
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.representative.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color="textSecondary"
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.course_student.student.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.course_student.course.group_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.status_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.amount}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              color="textSecondary"
                              sx={{ fontSize: "15px", fontWeight: "500" }}
                            >
                              {invoice.issued}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: "600" }}
                              >
                                {invoice.invoice_date}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="textSecondary">
                            No se encontraron facturas.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};

export default AppTableInvoices;
